<script setup>
import {defineEmits, defineModel, onMounted, onUnmounted, ref, watch,} from "vue";
import CrossCloseNotifySvg from "@/components/UI/svg/CrossCloseNotifySvg.vue";
import PlusCircleSvg from "@/components/UI/svg/PlusCircleSvg.vue";
import {Api} from "@/service/api";
import {SITE_URL} from "@/service/config";

const isVisible = defineModel('isVisible');
const accounts = ref([]);
const isLoading = ref(false);
const emit = defineEmits(['update:account']);

// Переменная для хранения ID интервала
let checkInterval = null;

function selectAcc(acc) {
    emit('update:account', acc);
    isVisible.value = false;
}

// Функция для загрузки аккаунтов
async function loadAccounts() {
    try {
        isLoading.value = true;
        accounts.value = await Api.getVkAccounts();

    } catch (error) {
        console.error('Ошибка загрузки аккаунтов:', error);
    } finally {
        isLoading.value = false;
    }
}

// Функция для старта проверки обновлений
// eslint-disable-next-line no-unused-vars
function startAccountCheck() {
    // Очищаем предыдущий интервал, если он существует
    if (checkInterval) {
        clearInterval(checkInterval);
    }

    // Запускаем проверку каждые 0.5 секунды
    checkInterval = setInterval(async () => {
        try {
            const prevCount = accounts.value.length;
            const newAccounts = await Api.getVkAccounts();

            // Если количество аккаунтов изменилось, обновляем список
            if (newAccounts.length !== prevCount) {
                accounts.value = newAccounts;

                // Если добавился новый аккаунт, автоматически выбираем его
                if (newAccounts.length > prevCount) {
                    const newAccount = newAccounts.find(acc =>
                        !accounts.value.find(oldAcc => oldAcc.id === acc.id)
                    );
                    if (newAccount) {
                        emit('update:account', newAccount);
                        isVisible.value = false; // Закрываем модалку
                    }
                    clearInterval(checkInterval);
                    checkInterval = null;
                }
                console.log('Список аккаунтов обновлен');
            }
        } catch (error) {
            console.error('Ошибка проверки аккаунтов:', error);
        }
    }, 500); // Проверяем каждые 0.5 секунды
}

// Функция для остановки проверки
function stopAccountCheck() {
    if (checkInterval) {
        clearInterval(checkInterval);
        checkInterval = null;
    }
}

function generateHex32() {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 32);
}

function attachAccount() {
    const params = new URLSearchParams({
        action: 'oauth2',
        response_type: 'code',
        client_id: "pAo3XFPaQlfG7C9s",
        state: generateHex32(),
        scope: "read_ads,read_payments",
        redirect_uri: SITE_URL + '/accounts/vk/ads',
    });

    const new_window_url = `https://ads.vk.com/hq/settings/access?${params.toString()}`;

    window.open(new_window_url);

    startAccountCheck()
}

// Следим за видимостью модалки
watch(isVisible, (newValue) => {
    if (newValue) {
        // При открытии модалки загружаем аккаунты
        loadAccounts();
        disablePageScroll();
    } else {
        // При закрытии модалки останавливаем проверку
        stopAccountCheck();
        enablePageScroll();
    }
});

onMounted(async () => {
    // Инициализируем загрузку аккаунтов
    await loadAccounts();

    // Добавляем обработчики для блокировки скролла
    document.addEventListener('wheel', handleWheel, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
    // Очищаем интервал при размонтировании
    stopAccountCheck();

    // Убираем обработчики
    document.removeEventListener('wheel', handleWheel);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('keydown', handleKeyDown);

    // Разблокируем скролл
    if (isVisible.value) {
        enablePageScroll();
    }
});

// Функция для блокировки скролла страницы (но не внутри модалки)
const disablePageScroll = () => {
    // Сохраняем текущую позицию скролла
    const scrollY = window.scrollY;

    // Блокируем скролл страницы
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    // Не блокируем overflow, чтобы скролл внутри модалки работал
    document.body.style.overflow = 'visible';

    // Сохраняем позицию скролла для восстановления
    document.body.dataset.scrollY = scrollY;
};

// Функция для разблокировки скролла страницы
const enablePageScroll = () => {
    // Восстанавливаем стили
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';

    // Восстанавливаем позицию скролла
    const scrollY = document.body.dataset.scrollY || '0';
    window.scrollTo(0, parseInt(scrollY, 10));

    // Удаляем data-атрибут
    delete document.body.dataset.scrollY;
};

// Следим за изменениями видимости модалки
watch(isVisible, (newValue) => {
    if (newValue) {
        disablePageScroll();
    } else {
        enablePageScroll();
    }
});

// Блокируем скролл колесиком мыши только на оверлее
const handleWheel = (event) => {
    if (isVisible.value) {
        // Проверяем, происходит ли событие внутри модального окна
        const modalContent = document.querySelector('.modal-content-container');
        if (!modalContent || !modalContent.contains(event.target)) {
            event.preventDefault();
        }
    }
};

// Блокируем скролл тач-жестами только на оверлее
const handleTouchMove = (event) => {
    if (isVisible.value) {
        // Проверяем, происходит ли событие внутри модального окна
        const modalContent = document.querySelector('.modal-content-container');
        if (!modalContent || !modalContent.contains(event.target)) {
            event.preventDefault();
        }
    }
};

// Предотвращаем скролл страницы клавишами Space, PageUp, PageDown, Home, End
const handleKeyDown = (event) => {
    if (isVisible.value && !event.target.closest('.modal-content-container')) {
        const keys = ['Space', 'PageUp', 'PageDown', 'Home', 'End'];
        if (keys.includes(event.code)) {
            event.preventDefault();
        }
    }
};

async function deleteVkAcc(vk_acc_id) {
    await Api.deleteVkAcc(vk_acc_id);
    const fetchedAccounts = await Api.getVkAccounts();
    accounts.value = fetchedAccounts;

    // Выбрать первый аккаунт по умолчанию, если есть
    if (fetchedAccounts.length > 0) {
        emit('update:account', fetchedAccounts[0]);
    }
    isVisible.value = false;
}


onMounted(async () => {
    // Добавляем обработчики для блокировки скролла
    document.addEventListener('wheel', handleWheel, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('keydown', handleKeyDown);

    accounts.value = await Api.getVkAccounts()

});

onUnmounted(() => {
    // Убираем обработчики при размонтировании
    document.removeEventListener('wheel', handleWheel);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('keydown', handleKeyDown);

    // Разблокируем скролл страницы при размонтировании компонента
    if (isVisible.value) {
        enablePageScroll();
    }
});

function closeModal() {
    isVisible.value = false;
}
</script>

<template>
    <!-- Overlay -->
    <div
        class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 transition-opacity duration-200"
        :class="{
            'hidden': !isVisible,
            'opacity-100': isVisible,
            'opacity-0': !isVisible
        }"
        @click.self="closeModal"
    >
        <!-- Modal Container -->
        <div
            class="modal-content-container bg-white rounded-xl w-full max-w-4xl mx-auto flex flex-col shadow-2xl transition-all duration-200 max-h-[90vh]"
            :class="{
                'translate-y-0 opacity-100 scale-100': isVisible,
                'translate-y-4 opacity-0 scale-95': !isVisible
            }"
        >
            <!-- Fixed Header -->
            <div class="p-4 sm:p-6 md:p-8 lg:p-10 border-b shrink-0 flex items-center justify-between">
                <div class="font-bold text-lg sm:text-xl md:text-2xl">
                    Менеджер аккаунтов
                </div>
                <button
                    type="button"
                    @click="closeModal"
                    class="ml-auto size-8 sm:size-10 flex items-center justify-center rounded-full cursor-pointer duration-150 transition-all hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    title="Закрыть окно"
                    aria-label="Закрыть модальное окно"
                >
                    <CrossCloseNotifySvg class="size-6 sm:size-8"/>
                </button>
            </div>

            <!-- Scrollable Content - скролл работает ВНУТРИ модалки -->
            <div class="flex-1 overflow-y-auto scrollbar-thin">
                <div class="p-4 sm:p-6 md:p-8 lg:px-10">
                    <!-- Accounts List -->
                    <div class="space-y-2 sm:space-y-0">
                        <div
                            v-for="acc in accounts"
                            :key="acc"
                            v-on:click="selectAcc(acc)"
                            class="flex flex-col sm:flex-row sm:items-center w-full gap-4 sm:gap-5 p-4 rounded-lg border sm:border-0 sm:border-b hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                        >
                            <!-- Avatar -->
                            <div class="flex items-center gap-4">
                                <div class="flex items-center gap-4">
                                    <div class="flex items-center justify-center size-12 sm:size-16 md:size-20 rounded-full bg-blue-500 flex-shrink-0">
                                        <div class="text-white font-bold text-lg">
                                            {{ acc.firstname[0] + " " + acc.lastname[0] }}
                                        </div>
                                    </div>
                                </div>
                                <div class="sm:hidden">
                                    <div class="text-base font-bold select-none truncate">
                                        {{ acc.firstname + " " + acc.lastname }}
                                    </div>
                                </div>
                            </div>

                            <!-- Desktop Info -->
                            <div class="hidden sm:block">
                                <div class="text-base md:text-lg font-bold select-none truncate max-w-xs lg:max-w-sm">
                                    {{ acc.firstname + " " + acc.lastname }}
                                </div>
                            </div>

                            <!-- Actions -->
                            <button
                                type="button"
                                class="ml-auto sm:ml-auto size-8 sm:size-10 flex items-center justify-center rounded-full cursor-pointer duration-150 transition-all hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 self-start sm:self-center"
                                title="Отвязать аккаунт"
                                aria-label="Отвязать аккаунт"
                                v-on:click="deleteVkAcc(acc.id)"
                            >
                                <CrossCloseNotifySvg class="size-5 sm:size-6 md:size-8"/>
                            </button>
                        </div>
                    </div>

                    <!-- Add Account Button -->
                    <div
                        class="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5 p-4 rounded-lg border sm:border-0 border-dashed mt-2 sm:mt-0 cursor-pointer duration-150 transition-all hover:bg-gray-50 active:bg-gray-100 group"
                        role="button"
                        tabindex="0"
                        v-on:click="attachAccount"
                        @keyup.enter="() => {}"
                    >
                        <div class="flex items-center gap-4">
                            <div class="size-12 sm:size-16 md:size-20 flex items-center justify-center">
                                <PlusCircleSvg class="size-10 sm:size-12 md:size-14 stroke-blue-500 group-hover:stroke-blue-600 transition-colors duration-150"/>
                            </div>
                            <div class="sm:hidden">
                                <div class="text-base font-bold select-none text-blue-500 group-hover:text-blue-600">
                                    Привязать аккаунт
                                </div>
                                <div class="text-sm text-gray-500 mt-1">
                                    Добавить новый аккаунт ВК рекламы
                                </div>
                            </div>
                        </div>

                        <!-- Desktop Text -->
                        <div class="hidden sm:block">
                            <div class="text-base md:text-lg font-bold select-none text-blue-500 group-hover:text-blue-600 transition-colors duration-150">
                                Привязать <span class="text-base md:text-lg font-bold select-none text-blue-500 group-hover:text-blue-600 transition-colors duration-150" v-if="accounts.length > 0">ещё один</span> аккаунт
                            </div>
                            <div class="text-sm text-gray-500 mt-1 max-w-xs lg:max-w-sm">
                                Добавить новый аккаунт ВК рекламы для отслеживания статистики
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer with Actions (optional) -->
            <div class="p-4 sm:p-6 md:p-8 lg:p-10 border-t shrink-0 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-end">
                <button
                    type="button"
                    @click="closeModal"
                    class="px-4 sm:px-6 py-2 sm:py-3 rounded-lg border border-gray-300 hover:bg-gray-50 duration-150 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm sm:text-base font-medium w-full sm:w-auto"
                >
                    Отмена
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Smooth scroll for modal content */
.scrollbar-thin {
    scrollbar-width: thin;
    -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
}

.scrollbar-thin::-webkit-scrollbar {
    width: 6px;
}

.scrollbar-thin::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: #555;
}

/* Improved focus styles for accessibility */
:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
}

/* Better touch targets on mobile */
@media (max-width: 640px) {
    button, [role="button"] {
        min-height: 44px;
        min-width: 44px;
    }
}

/* Animation for modal appearance */
@keyframes modalIn {
    from {
        opacity: 0;
        transform: translateY(10px) scale(0.98);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

/* Modal backdrop blur for modern browsers */
@media (prefers-reduced-motion: no-preference) {
    .bg-black\/50 {
        backdrop-filter: blur(4px);
    }
}

/* Prevent body scroll when modal is open, but allow scrolling inside modal */
body.modal-open {
    position: fixed;
    width: 100%;
    height: 100%;
}

/* Allow scrolling inside modal */
.modal-content-container {
    overscroll-behavior: contain; /* Предотвращает скролл страницы при достижении границ модалки */
}

/* Prevent text selection on overlay */
.fixed.inset-0 {
    user-select: none;
}

.fixed.inset-0 * {
    user-select: auto; /* Но внутри модалки разрешаем выделение текста */
}
</style>